import { useEffect, useRef, useState, type ReactNode, type FormEvent } from 'react';
import { ApiError } from '../api';

/** Shared pieces, after AgeDash's: small on purpose, every screen uses the same handful. */

export function PageHead({ title, sub, actions }: { title: string; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {sub ? <p className="sub">{sub}</p> : null}
      </div>
      {actions ? <div className="toolbar" style={{ marginBottom: 0 }}>{actions}</div> : null}
    </div>
  );
}

export function Note({ tone = 'info', children }: { tone?: 'info' | 'error' | 'success' | 'warn'; children: ReactNode }) {
  return (
    <div className={`note ${tone === 'info' ? '' : tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  const denied = error instanceof ApiError && error.status === 403;
  return <Note tone={denied ? 'warn' : 'error'}>{message}</Note>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <span className="spin" role="status" aria-label={label} />;
}

export function Chip({ tone = 'neutral', children }: { tone?: 'ok' | 'warn' | 'bad' | 'neutral' | 'accent'; children: ReactNode }) {
  return <span className={`chip ${tone}`}>{children}</span>;
}

export function StatusChip({ status }: { status: string }) {
  const tone =
    ['ok', 'active', 'completed', 'answered', 'in_progress', 'connected', 'published', 'live'].includes(status)
      ? 'ok'
      : ['failed', 'disabled', 'error', 'busy', 'no_answer', 'expired', 'missing'].includes(status)
        ? 'bad'
        : ['dialing', 'ringing', 'draft', 'disconnected', 'cancelled', 'pending'].includes(status)
          ? 'warn'
          : 'neutral';
  return <Chip tone={tone}>{status.replace(/_/g, ' ')}</Chip>;
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="stat">
      <div className="k">{label}</div>
      <div className="v">{value}</div>
      {hint ? <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{hint}</div> : null}
    </div>
  );
}

export function Field({ label, hint, htmlFor, children }: { label: string; hint?: ReactNode; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

export function Modal({
  title, sub, onClose, onSubmit, submitLabel = 'Save', busy, error, children, wide,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  onSubmit?: (e: FormEvent) => void;
  submitLabel?: string;
  busy?: boolean;
  error?: unknown;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref} style={wide ? { width: 'min(760px, 100%)' } : undefined}>
        <h2>{title}</h2>
        {sub ? <p className="sub" style={{ marginBottom: 18 }}>{sub}</p> : null}
        <ErrorNote error={error} />
        <form onSubmit={(e) => { e.preventDefault(); onSubmit?.(e); }}>
          {children}
          <div className="toolbar" style={{ marginTop: 18, marginBottom: 0, justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose}>
              {onSubmit ? 'Cancel' : 'Close'}
            </button>
            {onSubmit ? (
              <button type="submit" className="btn primary" disabled={busy}>
                {busy ? <Spinner label="Saving" /> : null} {submitLabel}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}

/** Data loading with the three states a screen actually has. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fn()
      .then((r) => live && setData(r))
      .catch((e) => live && setError(e))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);
  return { data, error, loading, reload: () => setNonce((n) => n + 1), setData };
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="empty">
      <Spinner /> {label}
    </div>
  );
}

export function useInterval(fn: () => void, ms: number | null) {
  const saved = useRef(fn);
  saved.current = fn;
  useEffect(() => {
    if (ms === null) return;
    const id = window.setInterval(() => saved.current(), ms);
    return () => window.clearInterval(id);
  }, [ms]);
}

/** The voice mark (the same artwork as the browser tab icon). */
export function BrandMark({ size = 26 }: { size?: number }) {
  return <img className="brand-mark" src="/favicon.svg" alt="" width={size} height={size} />;
}
